import { Modal } from "react-bootstrap-v5"
import { KTSVG } from "../../../helpers/KTSVG";
import { ExpertiseListModel } from "../../../hooks/partners/models/ExpertiseListModel";
import { defaultProfileImage } from "../../../utils/constants";
import { useState } from "react";
import CreateExpertiseModal from "../../managePartners/components/modals/CreateExpertiseModal";
import { useExpertiseDetails } from "../../../hooks/partners/useExpertiseDetails";
import { LoaderOverlay } from "../../../hooks/loader/LoaderOverlay";
import { formatPhone } from "../../../helpers/FormatInputs";
import { ExpertiseActionModal, ExpertiseActionType, ExpertiseActionPayload } from "./ExpertiseActionModal";
import { approveExpertiseAdmin, rejectExpertiseAdmin } from "../../../services/partnerExpertService";
import { showErrorAlert } from "../../../helpers/alert";

type Props = {
    show: boolean;
    onClose: () => void;
    record: ExpertiseListModel;
    // PR-I-87 — al aprobar/rechazar refrescamos la tabla del padre para que el service salga
    // del bucket "New" y aparezca en "Active"/"Reject". Opcional para no romper otros callers.
    onModerated?: () => void;
}

export const DetailExpertiseDrawer = ({ show, onClose, record, onModerated }: Props) => {
    const [createRecord, setCreateRecord] = useState(false);
    const { loading, details, reload } = useExpertiseDetails(record.id);
    const [actionType, setActionType] = useState<ExpertiseActionType | null>(null);
    const [submitting, setSubmitting] = useState(false);

    // PR-I-87 — botones visibles solo mientras el service esta pending. Si backend ya lo movio a
    // active/rejected, ocultamos las acciones para evitar doble aprobacion o dobles rechazos.
    // Fallback a record.status por si el detalle aun no cargo (evita "flash" del boton).
    const currentStatus = details?.status ?? record.status;
    const isPending = currentStatus === 'pending';

    const handleAction = async (result: boolean, payload?: ExpertiseActionPayload) => {
        if(!result || !actionType) {
            setActionType(null);
            return;
        }
        const targetId = details?.id ?? record.id;
        try {
            setSubmitting(true);
            if(actionType === 'approve') {
                await approveExpertiseAdmin(targetId);
            }
            else {
                if(!payload?.reason_id) return;
                await rejectExpertiseAdmin(targetId, payload.reason_id, payload.note);
            }
            setActionType(null);
            await reload();
            onModerated?.();
        }
        catch (err: any) {
            const message = err?.response?.data?.detail
                ?? err?.response?.data?.reason_id?.[0]
                ?? 'The action could not be completed. Please try again.';
            showErrorAlert({ title: 'Action failed', message });
        }
        finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal show={show}
        onHide={onClose}
        backdrop='static'
        enforceFocus={false}
        dialogClassName='experience-detail-modal expertise-modal'
        contentClassName='experience-detail-content'
        backdropClassName='experience-detail-backdrop'
        className={`w-100`}>
            <div className={`admin-experience-details-container`}>
                {/* Header */}
                <div className="admin-experience-details-header">
                    <h2 className="admin-experience-details-title">
                        Expert Details
                    </h2>
                    <div className="rm-style-3 cursor-pointer" onClick={()=>onClose()}>
                        <KTSVG path="/media/svg/nobilis/closeX.svg" className="rm-style-4" />
                    </div>
                </div>
                {loading ? <LoaderOverlay visible/> : (
                    <>
                        <div className="w-100 d-flex">
                            <div className='tab-navigation w-100 gap-3 justify-content-end'>
                                <button className="tap-add-experience-2-btn-secondary" onClick={() => setCreateRecord(true)}>
                                    <KTSVG path='/media/svg/nobilis/calendar-edit.svg' />&nbsp;
                                    <span>Create Expertise</span>
                                </button>
                            </div>
                        </div>

                        <div className="detail-expertise-content">

                            <div className="user-info">
                                <div className="d-flex flex-row">
                                    <span className="tag-label">Assigned Admin</span>
                                    <div className="assigned-admin">
                                        <img src={ details?.assignedAdmin?.avatar ?? defaultProfileImage}/>
                                        <span>{details?.assignedAdmin?.name}</span>
                                    </div>
                                </div>

                                <div className="d-flex flex-column">
                                    <span className="tag-italic">
                                        #{details?.id}
                                    </span>
                                    <div className="author-expertise">
                                        <img src={details?.partner?.logo ?? defaultProfileImage} />
                                        <span>{details?.partner.companyName}</span>
                                    </div>
                                </div>

                                <div className="d-flex flex-row w-100">
                                    <div className="d-flex flex-column" style={{ flex: '1 0 0' }}>
                                        <span className="tag-label">Email</span>
                                        <span className="tag-value">{details?.partner.email ?? '-'}</span>
                                    </div>
                                    <div className="d-flex flex-column" style={{ flex: '1 0 0' }}>
                                        <span className="tag-label">Phone</span>
                                        <span className="tag-value">{details?.partner.phone ? formatPhone(details?.partner.phone) : '-'}</span>
                                    </div>
                                </div>

                                {/* PR-I-87 — chip de estado del service moderable. Reusa clase generica del proyecto
                                    y agrega variante segun status para que el admin vea de un vistazo si sigue
                                    pendiente, ya se aprobo o se rechazo (y en ese caso, con que motivo). */}
                                <div className="d-flex flex-row w-100 align-items-center" style={{ gap: '10px' }}>
                                    <span className="tag-label">Status</span>
                                    <span className={`expertise-status-chip expertise-status-chip--${currentStatus}`}>
                                        {details?.statusDisplay ?? currentStatus}
                                    </span>
                                    {currentStatus === 'rejected' && details?.rejectionReason && (
                                        <span className="tag-value" style={{ opacity: 0.7 }}>
                                            — {details.rejectionReason}
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="content-info">
                                <span className="tag-title">{details?.title}</span>
                                <div className="d-flex w-100 flex-column" style={{ gap: 'var(--Internal-Grouping, 15px)' }}>
                                    <span className="tag-value">
                                        {details?.description}
                                    </span>
                                    <div className="rate-info">
                                        <span className="tag-label">Rate</span>
                                        <div className="d-flex w-100 flex-row justify-content-between">
                                            <span className="tag-rate">${details?.rate}</span>
                                            <span className="tag-value">/ Hour</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* PR-I-87 — Approve / Decline. Solo se muestran mientras el service esta pending.
                                Mismo patron visual que IntroductionAdminDetails para consistencia. */}
                            {isPending && (
                                // PR-I-87 — reusamos las clases del popup de accion (Community/Approve modal)
                                // para que Decline y Approve mantengan la misma jerarquia visual entre
                                // drawer y confirmacion: outline negro para Decline, negro solido con
                                // flecha para Approve.
                                <div className="admin-community-action-modal-content__actions w-100">
                                    <button
                                        type="button"
                                        className="admin-community-action-modal-content__actions_secundary"
                                        disabled={submitting}
                                        onClick={() => setActionType('decline')}
                                    >
                                        Decline expertise
                                    </button>
                                    <button
                                        type="button"
                                        className="admin-community-action-modal-content__actions_primary"
                                        disabled={submitting}
                                        onClick={() => setActionType('approve')}
                                    >
                                        Approve expertise
                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="4" viewBox="0 0 14 4" fill="none">
                                            <path d="M8.5 0.379395L12 3.38354H0" stroke="white"/>
                                        </svg>
                                    </button>
                                </div>
                            )}

                        </div>
                    </>
                )}

            </div>

            <CreateExpertiseModal
                close={() => setCreateRecord(false)}
                show={createRecord}
                partner={details?.partner.id}
            />

            {actionType && (
                <ExpertiseActionModal
                    record={record}
                    type={actionType}
                    onClose={handleAction}
                />
            )}
        </Modal>
    )
}
